import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, getDoc, doc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../firebase-config';
import { tryNotify, parseNotificationEmails } from '../services/mailService';
import { contactReceivedAdmin, contactReceivedClient } from '../services/emailTemplates';

function ContactForm({ prefilledPackage = null }) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'bs';

  const [form, setForm] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    weddingDate: '',
    message: '',
    contactMethods: []
  });
  
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});
  const [settings, setSettings] = useState(null);

  // Fetch settings for email notification preferences
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'general');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setSettings(docSnap.data());
        }
      } catch (err) {
        console.error("Error fetching settings in ContactForm:", err);
      }
    };
    fetchSettings();
  }, []);

  // Prefill message when package is selected
  useEffect(() => {
    if (prefilledPackage) {
      const packageName = typeof prefilledPackage.naziv === 'object' 
        ? prefilledPackage.naziv[currentLang] 
        : prefilledPackage.naziv;
      
      const prefilledMessage = currentLang === 'bs'
        ? `Zdravo! Zainteresovan/a sam za paket "${packageName}". Molim Vas za više informacija i dostupnost.`
        : `Hello! I'm interested in the "${packageName}" package. Please send me more information and availability.`;
      
      setForm(prev => ({ ...prev, message: prefilledMessage }));
      
      // Scroll to contact form
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [prefilledPackage, currentLang]);

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t('contact.required');
    if (!form.email.trim()) newErrors.email = t('contact.required');
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) newErrors.email = 'Invalid email';
    if (!form.message.trim()) newErrors.message = t('contact.required');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setForm(prev => ({ ...prev, weddingDate: date }));
  };

  const handleContactMethodChange = (method) => {
    setForm(prev => {
      const methods = prev.contactMethods.includes(method)
        ? prev.contactMethods.filter(m => m !== method)
        : [...prev.contactMethods, method];
      return { ...prev, contactMethods: methods };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error(currentLang === 'bs' ? 'Molimo popunite obavezna polja' : 'Please fill in required fields');
      return;
    }
    
    setSending(true);
    const loadingToast = toast.loading(t('contact.sending'));

    try {
      // Save message to Firebase
      const messageData = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || '',
        weddingDate: form.weddingDate || '',
        message: form.message.trim(),
        contactMethods: form.contactMethods || [],
        packageName: prefilledPackage ? (typeof prefilledPackage.naziv === 'object' ? prefilledPackage.naziv[currentLang] : prefilledPackage.naziv) : '',
        status: 'new', // new, read, replied
        createdAt: serverTimestamp(),
        language: currentLang
      };

      await addDoc(collection(db, "messages"), messageData);

      // === EMAIL NOTIFIKACIJE ===
      // 1) Admin notifikacija — ako su konfigurisani notification emails i toggle je on
      const adminEmails = parseNotificationEmails(settings?.notificationEmails);
      const notifyAdminEnabled = settings?.notifyOnContact !== false; // default true
      if (adminEmails.length > 0 && notifyAdminEnabled) {
        const adminTpl = contactReceivedAdmin({
          name:                  messageData.name,
          email:                 messageData.email,
          phone:                 messageData.phone,
          weddingDate:           messageData.weddingDate,
          message:               messageData.message,
          packageName:           messageData.packageName,
          language:              messageData.language,
          contactMethods:        messageData.contactMethods,
          notificationRecipient: adminEmails,
        });
        // fire-and-forget, ne bloking-ujemo korisnikov UX
        tryNotify({
          to:       adminEmails,
          subject:  adminTpl.subject,
          html:     adminTpl.html,
          replyTo:  messageData.email,
          fromName: settings?.emailFromName,
        });
      }

      // 2) Klijent potvrda — ako je toggle on i klijent ima email
      const sendClientConfirm = settings?.sendClientConfirmations !== false; // default true
      if (sendClientConfirm && messageData.email) {
        const clientTpl = contactReceivedClient({
          name:     messageData.name,
          language: messageData.language,
        });
        tryNotify({
          to:       messageData.email,
          subject:  clientTpl.subject,
          html:     clientTpl.html,
          fromName: settings?.emailFromName,
        });
      }

      toast.success(t('contact.success'), { id: loadingToast });
      
      // Reset form
      setForm({ name: '', email: '', phone: '', weddingDate: '', message: '', contactMethods: [] });
      
    } catch (error) {
      console.error('Email send error:', error);
      toast.error(t('contact.error'), { id: loadingToast });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8 relative overflow-hidden">

      <div className="relative z-10">
        {prefilledPackage && (
          <div className="mb-8 p-4 bg-gray-50 rounded-2xl border border-gray-100 flex items-center gap-4 animate-fade-in">
             {prefilledPackage.slika && (
               <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 shadow-md">
                 <img 
                   src={prefilledPackage.slika.startsWith('http') || prefilledPackage.slika.startsWith('/') ? prefilledPackage.slika : `/img/photos/${prefilledPackage.slika}`} 
                   alt="" 
                   className="w-full h-full object-cover" 
                 />
               </div>
             )}
             <div>
                <p className="text-[10px] font-black text-gold-dark uppercase tracking-widest mb-1">Odabrani paket</p>
                <h4 className="text-xl font-black text-gray-900 uppercase tracking-tight">
                  {typeof prefilledPackage.naziv === 'object' ? prefilledPackage.naziv[currentLang] : prefilledPackage.naziv}
                </h4>
                {prefilledPackage.cijena && (
                  <p className="text-sm font-bold text-gray-500 mt-1">{prefilledPackage.cijena} {prefilledPackage.valuta}</p>
                )}
             </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 font-sans" noValidate>
          <div className="space-y-3">
            <label htmlFor="name" className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] ml-1 block">
              {t('contact.name')}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder={t('contact.namePlaceholder')}
              value={form.name}
              onChange={handleChange}
              required
              aria-invalid={errors.name ? 'true' : 'false'}
              className={`landing-input w-full ${errors.name ? 'border-red-500' : ''}`}
            />
            {errors.name && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-3">
              <label htmlFor="email" className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] ml-1 block">
                {t('contact.email')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder={t('contact.emailPlaceholder')}
                value={form.email}
                onChange={handleChange}
                required
                aria-invalid={errors.email ? 'true' : 'false'}
                className={`landing-input w-full ${errors.email ? 'border-red-500' : ''}`}
              />
              {errors.email && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.email}</p>}
            </div>

            <div className="space-y-3">
              <label htmlFor="phone" className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] ml-1 block">
                {t('contact.phone')}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder="+387 61 000 000"
                value={form.phone}
                onChange={handleChange}
                className="landing-input w-full"
              />
            </div>
          </div>

          <div className="space-y-3">
            <label htmlFor="weddingDate" className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] ml-1 block">
              {t('contact.weddingDate')}
            </label>
            <input
              type="date"
              id="weddingDate"
              name="weddingDate"
              value={form.weddingDate}
              onChange={handleDateChange}
              min={new Date().toISOString().split('T')[0]}
              className="landing-input w-full"
            />
          </div>

          {form.phone && (
            <div className="space-y-3 p-5 bg-gray-50 rounded-2xl border border-gray-100">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] block">
                {t('contact.contactMethod')}
              </label>
              <div className="flex flex-wrap gap-2" role="group" aria-label={t('contact.contactMethod')}>
                {['email', 'viber', 'whatsapp', 'telegram', 'sms'].map((method) => {
                  const icons = {
                    email: '📧',
                    viber: '💜',
                    whatsapp: '💚',
                    telegram: '✈️',
                    sms: '📱'
                  };
                  return (
                    <label key={method} className={`flex items-center gap-2 px-5 py-3 rounded-2xl border-2 cursor-pointer transition-all ${
                      form.contactMethods.includes(method) 
                        ? 'bg-black border-black text-white shadow-xl scale-105' 
                        : 'bg-white border-gray-100 text-gray-600 hover:border-gray-300'
                    }`}>
                      <input
                        type="checkbox"
                        checked={form.contactMethods.includes(method)}
                        onChange={() => handleContactMethodChange(method)}
                        className="sr-only"
                      />
                      <span className="text-lg">{icons[method]}</span>
                      <span className="text-xs font-black uppercase tracking-widest">{method}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <label htmlFor="message" className="text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] ml-1 block">
              {t('contact.message')}
            </label>
            <textarea
              id="message"
              name="message"
              rows="5"
              placeholder={t('contact.messagePlaceholder')}
              value={form.message}
              onChange={handleChange}
              required
              aria-invalid={errors.message ? 'true' : 'false'}
              className={`landing-input w-full resize-none ${errors.message ? 'border-red-500' : ''}`}
            ></textarea>
            {errors.message && <p className="text-red-500 text-[10px] mt-1 font-bold">{errors.message}</p>}
          </div>

          <div className="flex justify-center pt-3">
            <button 
              type="submit" 
              disabled={sending}
              className="bg-gray-900 text-white px-12 py-4 rounded-full font-black uppercase tracking-[0.3em] text-xs hover:bg-gold hover:text-obsidian active:scale-95 transition-all shadow-xl disabled:opacity-50"
            >
              {sending ? t('contact.sending') : t('contact.send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContactForm;